import { supabase } from '../supabase/client';

export type AppearanceTheme = 'light' | 'dark' | 'system';

export interface UserPreferences {
  id?: string;
  user_id: string;
  email_notifications: boolean;
  sms_notifications: boolean;
  push_notifications: boolean;
  case_assignment_notifications: boolean;
  customer_message_notifications: boolean;
  system_announcement_notifications: boolean;
  do_not_disturb_enabled: boolean;
  do_not_disturb_start_time: string;
  do_not_disturb_end_time: string;
  location_tracking: boolean;
  data_sharing: boolean;
  /** 與 ThemeContext 同步；NULL 表示未寫入，沿用裝置 localStorage */
  appearance_theme?: AppearanceTheme | null;
  monthly_budget?: number | null;
  budget_alert_threshold?: number | null;
  budget_notifications_enabled?: boolean;
  current_location?: { lat: number; lng: number } | null;
  created_at?: string;
  updated_at?: string;
}

export class UserPreferencesService {
  /**
   * 獲取用戶偏好設定
   */
  static async getUserPreferences(userId: string): Promise<UserPreferences | null> {
    try {
      const { data, error } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        if (error.code === 'PGRST116') {
          // 沒有記錄，返回 null（將使用默認值）
          return null;
        }
        if (error.code === 'PGRST205' || error.code === 'PGRST406') {
          // 表不存在或 RLS 策略問題，返回 null（將使用默認值）
          console.warn('user_preferences 表不可用（表不存在或 RLS 策略限制），使用默認設定');
          return null;
        }
        // ✅ 添加：處理 HTTP 406 錯誤
        if (error.message?.includes('406') || error.message?.includes('Not Acceptable')) {
          console.warn('user_preferences 查詢被拒絕（406），可能是 RLS 策略問題，使用默認設定');
          return null;
        }
        console.error('Error fetching user preferences:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error fetching user preferences:', error);
      return null;
    }
  }

  /**
   * 創建或更新用戶偏好設定（經 DB RPC merge_user_preferences，避免 RLS 下 upsert 403）
   */
  static async createOrUpdatePreferences(
    userId: string,
    preferences: Partial<UserPreferences>
  ): Promise<UserPreferences> {
    try {
      const {
        data: { user },
        error: authErr,
      } = await supabase.auth.getUser();
      if (authErr || !user?.id) {
        throw new Error('請先登入');
      }
      if (user.id !== userId) {
        throw new Error('無權修改此帳號的偏好設定');
      }

      const { id: _id, user_id: _uid, created_at: _c, updated_at: _u, ...patch } =
        preferences as Record<string, unknown>;

      const p_patch: Record<string, unknown> = { ...patch };
      Object.keys(p_patch).forEach((k) => {
        if (p_patch[k] === undefined) delete p_patch[k];
      });

      const { data, error } = await supabase.rpc('merge_user_preferences', {
        p_patch: p_patch,
      });

      if (error) {
        console.error('Error saving user preferences:', error);
        throw new Error('保存偏好設定失敗');
      }

      const row = Array.isArray(data) ? data[0] : data;
      if (!row) {
        throw new Error('保存偏好設定失敗');
      }
      return row as UserPreferences;
    } catch (error: any) {
      console.error('Error saving user preferences:', error);
      throw error;
    }
  }

  /**
   * 更新通知設定
   */
  static async updateNotificationSettings(
    userId: string,
    settings: {
      email_notifications?: boolean;
      sms_notifications?: boolean;
      push_notifications?: boolean;
      case_assignment_notifications?: boolean;
      customer_message_notifications?: boolean;
      system_announcement_notifications?: boolean;
    }
  ): Promise<UserPreferences> {
    return this.createOrUpdatePreferences(userId, settings);
  }

  /**
   * 更新勿擾時間設定
   */
  static async updateDoNotDisturbSettings(
    userId: string,
    settings: {
      do_not_disturb_enabled?: boolean;
      do_not_disturb_start_time?: string;
      do_not_disturb_end_time?: string;
    }
  ): Promise<UserPreferences> {
    return this.createOrUpdatePreferences(userId, settings);
  }

  /**
   * 更新隱私設定
   */
  static async updatePrivacySettings(
    userId: string,
    settings: {
      location_tracking?: boolean;
      data_sharing?: boolean;
    }
  ): Promise<UserPreferences> {
    return this.createOrUpdatePreferences(userId, settings);
  }

  /**
   * 更新預算設定
   */
  static async updateBudgetSettings(
    userId: string,
    settings: {
      monthly_budget?: number | null;
      budget_alert_threshold?: number | null;
      budget_notifications_enabled?: boolean;
    }
  ): Promise<UserPreferences> {
    return this.createOrUpdatePreferences(userId, settings);
  }

  /**
   * 獲取用戶預算設定
   */
  static async getBudgetSettings(userId: string): Promise<{
    monthlyBudget: number;
    budgetAlertThreshold: number;
    budgetNotificationsEnabled: boolean;
  }> {
    const preferences = await this.getUserPreferences(userId);
    return {
      monthlyBudget: preferences?.monthly_budget ?? 10000,
      budgetAlertThreshold: preferences?.budget_alert_threshold ?? 80,
      budgetNotificationsEnabled: preferences?.budget_notifications_enabled ?? true,
    };
  }

  /**
   * 更新当前位置
   */
  static async updateCurrentLocation(
    userId: string,
    location: { lat: number; lng: number } | null
  ): Promise<UserPreferences> {
    return this.createOrUpdatePreferences(userId, { current_location: location });
  }

  /**
   * 获取当前位置
   */
  static async getCurrentLocation(userId: string): Promise<{ lat: number; lng: number } | null> {
    const preferences = await this.getUserPreferences(userId);
    return preferences?.current_location ?? null;
  }
}
